import Blue from '~/core';

const CATEGORY_TO_CMV2 = {
  functional: ['functionality_storage', 'personalization_storage'],
  performance: ['analytics_storage'],
  marketing: ['ad_storage', 'ad_user_data', 'ad_personalization'],
};

function buildConsentUpdate(prefs) {
  const update = {};
  Object.keys(CATEGORY_TO_CMV2).forEach((category) => {
    const value = prefs[category] ? 'granted' : 'denied';
    CATEGORY_TO_CMV2[category].forEach((key) => {
      update[key] = value;
    });
  });
  return update;
}

function pushConsentUpdate(prefs) {
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  gtag('consent', 'update', buildConsentUpdate(prefs));
}

async function savePreferences(prefs) {
  const response = await fetch('/api/cookies/consent-prefs', {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
    body: JSON.stringify({
      functional: prefs.functional,
      performance: prefs.performance,
      marketing: prefs.marketing,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to persist consent preferences: HTTP ${response.status}`);
  }

  pushConsentUpdate(prefs);
}

new Blue({
  name: 'site modals',
  selector: '[data-cookie-consent]',
  setup(root) {
    const banner = root.querySelector('[data-cookie-banner]');
    const modal = root.querySelector('[data-cookie-modal]');

    if (!banner || !modal) {
      return;
    }

    const continueBtn = banner.querySelector('[data-cookie-continue]');
    const manageBtn = banner.querySelector('[data-cookie-manage]');
    const bannerClose = banner.querySelector('[data-cookie-banner-close]');

    const rejectAllBtn = modal.querySelector('[data-cookie-reject-all]');
    const confirmBtn = modal.querySelector('[data-cookie-confirm]');
    const modalClose = modal.querySelector('[data-cookie-modal-close]');
    const categoryToggles = modal.querySelectorAll('[data-cookie-category]');

    const dismissBanner = () => {
      banner.addEventListener('transitionend', function close() {
        banner.removeEventListener('transitionend', close);
        banner.classList.add('site-modals__modal--dismissed');
      });
      banner.classList.remove('site-modals__modal--active');
    };

    const openModal = () => {
      modal.hidden = false;
      // double-rAF so the transition runs from a known starting state
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          modal.classList.add('site-modals__modal--active');
        });
      });
    };

    const closeModal = () => {
      modal.addEventListener('transitionend', function close() {
        modal.removeEventListener('transitionend', close);
        modal.classList.add('site-modals__modal--dismissed');
        modal.hidden = true;
      });
      modal.classList.remove('site-modals__modal--active');
    };

    const readModalToggleState = () => {
      const prefs = {
        functional: false,
        performance: false,
        marketing: false,
      };
      categoryToggles.forEach((input) => {
        const category = input.getAttribute('data-cookie-category');
        if (category in prefs) {
          prefs[category] = input.checked;
        }
      });
      return prefs;
    };

    // Show banner after a short delay so it doesn't compete with first paint
    setTimeout(() => {
      banner.classList.add('site-modals__modal--active');
    }, 750);

    // Banner X — accept all non-essential (same as Continue); dismiss regardless of save result
    if (bannerClose) {
      this.$addEvents(bannerClose, 'click enter', () => {
        dismissBanner();
        savePreferences({ functional: true, performance: true, marketing: true }).catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        });
      });
    }

    // Continue — grant all non-essential
    if (continueBtn) {
      this.$addEvents(continueBtn, 'click enter', async () => {
        try {
          await savePreferences({ functional: true, performance: true, marketing: true });
          dismissBanner();
        } catch (err) {
          // Keep the banner up so the user can retry. Log for diagnostics.
          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
    }

    // Manage Cookies — close banner, open modal
    if (manageBtn) {
      this.$addEvents(manageBtn, 'click enter', () => {
        dismissBanner();
        openModal();
      });
    }

    // Modal X — accept all non-essential (same as Continue); close regardless of save result
    if (modalClose) {
      this.$addEvents(modalClose, 'click enter', () => {
        closeModal();
        savePreferences({ functional: true, performance: true, marketing: true }).catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        });
      });
    }

    // Reject All — deny all non-essential regardless of toggle state
    if (rejectAllBtn) {
      this.$addEvents(rejectAllBtn, 'click enter', async () => {
        try {
          await savePreferences({ functional: false, performance: false, marketing: false });
          closeModal();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
    }

    // Confirm Choices — save current toggle states
    if (confirmBtn) {
      this.$addEvents(confirmBtn, 'click enter', async () => {
        try {
          const prefs = readModalToggleState();
          await savePreferences(prefs);
          closeModal();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
    }
  },
});
